<template>
  <div>
    <PageAllow controller="AllianceBusinesses" action="index" />
    <Breadcrumbs :items="items" />

    <v-slide-x-transition>
      <v-sheet v-show="!currentItemMode" class="transparent-background-color">
        <v-row dense class="mx-5">
          <v-col cols="12" sm="12" md="4">
            <v-row dense align="center" class="pa-1">
              <SearcherKeyword
                ref="keyword"
                v-model="options.keyword"
                @text="alliancesKeyword = $event"
                :label="
                  $t('Alliances.alliancesKeyword', [$consts.allianceName()])
                "
              />
            </v-row>
          </v-col>
          <v-col cols="12" sm="12" md="8" align-self="center">
            <v-row dense align="center" class="search-controls">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="px-0 py-0 caption font-weight-bold grey--text"
                    color="primary"
                    @click.stop="onSearch()"
                  >
                    <v-icon small class="pe-1">fa-search</v-icon>
                    {{ $t("Search.search") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.searchTip") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    small
                    text
                    class="caption font-weight-bold grey--text"
                    @click.stop="resetSearch()"
                  >
                    <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                    {{ $t("Search.reset") }}
                  </v-btn>
                </template>
                <span>{{ $t("Search.resetTip") }}</span>
              </v-tooltip>
              <v-divider vertical />
              <v-btn
                class="caption font-weight-bold grey--text"
                :class="
                  $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                "
                small
                text
                @click.stop="$store.commit('toggleSearchPanel')"
              >
                {{ $t("Search.pro") }}
                <v-icon small class="ps-1">{{
                  $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
                }}</v-icon>
              </v-btn>
              <v-spacer />
              <v-btn
                v-if="$permission.permissionAllow('AllianceBusinesses', 'add')"
                small
                color="primary"
                @click.stop="
                  currentItemMode = 'new';
                  currentItem = { id: 0 };
                "
                class="ms-1"
              >
                <v-icon x-small class="pe-1">fa-plus</v-icon>
                {{ $t("CRUD.newInfo", [$consts.allianceName()]) }}
              </v-btn>
              <v-btn
                v-if="$permission.permissionAllow('AllianceBusinesses', 'export')"
                small
                color="print"
                dark
                class="ms-1"
                @click.stop="exportExcel()"
              >
                <v-icon x-small class="pe-1">fa-file-export</v-icon>
                {{ $t("Export.export", [$consts.allianceName()]) }}
              </v-btn>
            </v-row>
          </v-col>
        </v-row>
        <v-expansion-panels
          v-model="$store.getters.panels"
          multiple
          flat
          accordion
        >
          <v-expansion-panel class="transparent-background-color">
            <v-expansion-panel-content>
              <v-row dense align="center">
                <v-col cols="12" sm="6" md="4">
                  <SearcherDistrict
                    v-model="districtSelected"
                    :label="
                      $t('Alliances.Fields.alliance_district', [
                        $consts.allianceName(),
                      ])
                    "
                  />
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div
                    class="caption grey--text input-chip-group-label"
                    :class="
                      $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
                    "
                  >
                    {{ $t("Alliances.Fields.status") }}
                  </div>
                  <v-chip-group
                    v-model="options.status"
                    column
                    multiple
                    class="mx-1"
                  >
                    <v-chip
                      small
                      label
                      filter
                      class="mx-1"
                      color="primary"
                      text-color="white"
                      :key="1"
                      :value="1"
                      >{{ $t("Alliances.status.on") }}</v-chip
                    >
                    <v-chip
                      small
                      label
                      filter
                      class="mx-1"
                      color="grey"
                      text-color="white"
                      :key="0"
                      :value="0"
                      >{{ $t("Alliances.status.off") }}</v-chip
                    >
                  </v-chip-group>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="mx-4 mb-4">
          <v-data-table
            :headers="headers"
            :items="alliances"
            :loading="loading"
            :options.sync="options"
            :page.sync="paging.page"
            :server-items-length="paging.count"
            multi-sort
            class="elevation-2 px-4 pt-1 sticky-table"
            id="sticky-table"
            :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
            fixed-header
            :footer-props="$t('$config.footer-props')"
          >
            <template v-slot:header.actions="{ header }">
              {{ header.text }}
              <v-icon x-small>fa-sliders-h</v-icon>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                small
                label
                :color="item.status | alliancesStatusColor"
                :text-color="item.status | alliancesStatusTextColor"
              >
                {{ item.status | alliancesStatusText }}
              </v-chip>
            </template>
            <template v-slot:item.shop_count="{ item }">
              <a
                style="text-decoration: none"
                @click="
                  shopDialog = true;
                  currentItem = item;
                "
              >
                {{ item.shop_count }}
              </a>
            </template>
            <template v-slot:item.actions="{ item, index }">
              <span :v-resize="!index ? $stickTableResizer() : null"> </span>
              <v-tooltip
                top
                v-if="$permission.permissionAllow('AllianceBusinesses', 'view')"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="secondary"
                    class="px-1 py-4 me-1"
                    @click.stop="
                      currentItemMode = 'view';
                      currentItem = item;
                    "
                    >fa-file-alt
                  </v-icon>
                </template>
                <span>{{ $t("CRUD.viewInfo", [$consts.allianceName()]) }}</span>
              </v-tooltip>
              <v-tooltip
                top
                v-if="$permission.permissionAllow('AllianceBusinesses', 'edit')"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="primary"
                    class="px-1 py-4 me-1"
                    @click.stop="
                      currentItemMode = 'edit';
                      currentItem = item;
                    "
                    >fa-pencil-alt
                  </v-icon>
                </template>
                <span>{{ $t("CRUD.editInfo", [$consts.allianceName()]) }}</span>
              </v-tooltip>
              <v-menu
                v-model="item.btnMenu"
                open-on-hover
                close-on-click
                close-on-content-click
                offset-y
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    color="actionMore"
                    class="px-1"
                    >fa-ellipsis-v
                  </v-icon>
                </template>
                <v-list dense>
                  <!-- 销售管理下属门店 -->
                  <v-list-item :href="'/#/alliances/edit-shop/' + item.id">
                    <v-list-item-title>
                      {{
                        $t("Alliances.actions.manage_shop", [
                          $consts.shopName(),
                        ])
                      }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </div>
      </v-sheet>
    </v-slide-x-transition>
    <AllianceDetail
      v-show="currentItemMode"
      v-model="currentItem"
      :mode="currentItemMode"
      v-on:mode="currentItemMode = $event"
      v-on:save="getAlliancesFromAPI()"
    />
    <ShopDialog
      v-model="shopDialog"
      :alliance="currentItem"
    />
  </div>
</template>

<script>
import i18n from "@/i18n";
import { mapState } from "vuex";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import Alliances from "@/api/alliance_businesses";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherDistrict from "@/components/plugins/SearcherDistrict";
import AllianceDetail from "./plugins/Detail";
import ShopDialog from "./plugins/ShopDialog"

const defaultOptions = {
  keyword: "",
  login_user_id: "",
  status: [0, 1],
  district_ids: [],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
    SearcherDistrict,
    AllianceDetail,
    ShopDialog,
  },

  computed: {
    items() {
      return [
        {
          text: i18n.t("Nav.home"),
          disabled: false,
          href: "/home",
        },
        {
          text: i18n.t("Nav.shopManagement", [this.$consts.shopName()]),
          disabled: false,
          href: "/shops",
        },
        {
          text: i18n.t("Nav.alliances", [this.$consts.allianceName()]),
          disabled: true,
          href: "/alliances",
        },
      ];
    },

    headers() {
      return [
        {
          text: i18n.t("Alliances.Fields.alliance_business_code", [
            this.$consts.allianceName(),
          ]),
          align: "start",
          sortable: false,
          class: "text-no-wrap",
          value: "alliance_business_code",
        },
        {
          text: i18n.t("Alliances.Fields.alliance_business_name", [
            this.$consts.allianceName(),
          ]),
          align: "start",
          sortable: false,
          class: "text-no-wrap",
          value: "alliance_business_name",
        },
        {
          text: i18n.t("Alliances.Fields.alliance_district", [
            this.$consts.allianceName(),
          ]),
          align: "start",
          sortable: false,
          class: "text-no-wrap",
          value: "district.name",
        },
        {
          text: i18n.t("Alliances.Fields.alliance_user_name"),
          align: "center",
          sortable: false,
          class: "text-no-wrap",
          value: "account_user.name",
        },
        {
          text: i18n.t("Alliances.Fields.shop_count", [
            this.$consts.shopName(),
          ]),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "shop_count",
        },
        {
          text: i18n.t("Alliances.Fields.status"),
          align: "center",
          class: "text-no-wrap",
          sortable: false,
          value: "status",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          class: "text-no-wrap",
          sortable: false,
          value: "actions",
        },
      ];
    },
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "stickTableCols"]),
  },

  mounted() {
    this.$commonUtils.loadOptions("alliances", ["districtsSelected"]).then(
      function (data) {
        if (data.options) {
          this.options = data.options;
        } else this.resetSearch();

        data.selecteds.forEach((selected) => {
          switch (selected.key) {
            case "districtsSelected":
              this.districtsSelected = selected.value;
              break;
          }
        });
      }.bind(this)
    );
  },

  data: () => ({
    alliances: [],
    alliancesKeyword: "",
    districtSelected: null,
    options: JSON.parse(
      sessionStorage.getItem("alliances-options")
        ? sessionStorage.getItem("alliances-options")
        : JSON.stringify(defaultOptions)
    ),
    paging: {},
    currentItemMode: null,
    currentItem: null,
    shopDialog: false,
  }),

  methods: {
    resetSearch() {
      this.districtSelected = null;
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.keyword].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        keyword: this.alliancesKeyword,
      });
    },
    getAlliancesFromAPI() {
      Alliances.fetchAll(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.alliances = response.data.alliance_businesses;
            this.paging = response.data.paging;
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
    exportExcel() {
      Alliances.exportExcel(this.options);
    },
  },

  watch: {
    $route() {
      if (!this.$route.query.id) {
        this.currentItemMode = null;
        this.currentItem = null;
      }
    },
    currentItem: {
      handler() {
        if (this.currentItem) {
          this.$router.replace("/alliances?id=" + this.currentItem.id);
          this.$cacheScrollLocation();
        } else {
          this.$scrollToCachedLocation();
          this.$router.replace("/alliances");
        }
      },
    },
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getAlliancesFromAPI();
        sessionStorage.setItem(
          "alliances-options",
          JSON.stringify(this.options)
        );
      },
    },
    districtSelected: {
      handler() {
        this.options.district_ids = this.districtSelected
          ? [this.districtSelected.id]
          : "";
        sessionStorage.setItem(
          "alliances-districtsSelected",
          JSON.stringify(this.districtSelected)
        );
      },
    },
  },
};
</script>